import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import ChartTooltip from '@/views/deepDiveView/ChartToolTip'
import { fmtTime } from '@/views/deepDiveView/TimeWindow'
import { METRICS, METRIC_LINE_SERIES, type ChartPoint, type MetricKey } from '@/views/deepDiveView/types'

const AXIS_TICK = { fontSize: 9, fill: 'var(--t-2)' as const }

export type SeverityMarker = { ts: number; severity: 'yellow' | 'red' }

type Props = {
  data: ChartPoint[]
  metricKey: MetricKey
  height?: number
  severityMarkers?: SeverityMarker[]
}

export default function MetricChart({ data, metricKey, height = 140, severityMarkers = [] }: Props) {
  const def = METRICS.find((m) => m.key === metricKey)
  const lines = METRIC_LINE_SERIES[metricKey] ?? []
  if (!def) return null

  // Recharts 3.x throws when `XAxis type="number" domain={['dataMin','dataMax']}`
  // is mounted with an empty data array (both bounds resolve to ±Infinity).
  // Render a placeholder until at least one point arrives.
  if (!data.length) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--t-2)',
          fontSize: 11,
          border: '0.5px dashed var(--bd-1)',
          borderRadius: 'var(--radius)',
        }}
      >
        {def.label}: no data in window
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data as ChartPoint[]} margin={{ top: 4, right: 40, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.04)" />
        <XAxis
          dataKey="ts"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(v) => fmtTime(Number(v))}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId={def.axis}
          domain={def.axis === 'ratio' ? [0, 'auto'] : ['auto', 'auto']}
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={def.axis === 'kbps' ? 40 : 36}
        />
        <Tooltip
          content={<ChartTooltip />}
          labelFormatter={(v) => fmtTime(Number(v))}
        />
        {lines.map((ln) => (
          <Line
            key={ln.dataKey}
            yAxisId={def.axis}
            dataKey={ln.dataKey}
            name={ln.label}
            stroke={ln.color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls
            strokeDasharray={ln.dash ?? def.dash}
          />
        ))}
        {severityMarkers.map((m, i) => (
          <ReferenceLine
            key={`sev-${i}`}
            x={m.ts}
            yAxisId={def.axis}
            stroke={m.severity === 'red' ? 'var(--crit)' : 'var(--warn)'}
            strokeDasharray="3 3"
            strokeWidth={1}
            label=""
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}
